import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, Ruler, Truck, RotateCcw, ShieldCheck, ChevronRight, Eye, Flame, Layers } from "lucide-react";
import { getProduct, getRecommendations, getPairings, money } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const PairTile = ({ p, highlight, onAdd }) => (
  <div className={`w-36 sm:w-44 ${highlight ? "ring-1 ring-white/40" : ""}`}>
    <Link to={`/product/${p.handle}`} className="block aspect-[3/4] bg-[#141414] overflow-hidden">
      <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
    </Link>
    <div className="pt-2">
      <p className="font-display text-[11px] uppercase tracking-tight leading-tight truncate">{p.title}</p>
      <div className="flex items-center justify-between mt-1">
        <span className="font-mono text-xs">{money(p.price)}</span>
        {highlight
          ? <span className="overline text-white/40">This</span>
          : <button onClick={onAdd} data-testid={`pair-add-${p.handle}`} className="overline text-white/70 hover:text-white">+ Add</button>}
      </div>
    </div>
  </div>
);

export default function Product() {
  const { handle } = useParams();
  const { addToCart, toggleWishlist, isWished, pushRecent, recent, setSizeFinderOpen } = useStore();
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [showSticky, setShowSticky] = useState(false);
  const addBtnRef = useRef(null);

  const { data: product, isLoading } = useQuery({ queryKey: ["product", handle], queryFn: () => getProduct(handle) });
  const { data: recs = [] } = useQuery({ queryKey: ["recs", handle], queryFn: () => getRecommendations({ handle, limit: 4 }), enabled: !!handle });
  const { data: pairs = [] } = useQuery({ queryKey: ["pairs", handle], queryFn: () => getPairings(handle), enabled: !!handle });

  useEffect(() => { window.scrollTo(0, 0); setActiveImg(0); setSize(null); }, [handle]);
  useEffect(() => {
    const el = addBtnRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setShowSticky(!e.isIntersecting), { threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [handle, size, product]);

  const viewers = useMemo(() => 9 + Math.floor(Math.random() * 33), [handle]);
  const stockLeft = useMemo(() => 2 + Math.floor(Math.random() * 6), [handle]);
  useEffect(() => {
    if (product) {
      pushRecent(product.handle);
      setColor(product.colors?.[0] || null);
    }
  }, [product]); // eslint-disable-line

  const { data: recentProducts = [] } = useQuery({
    queryKey: ["recent", recent.join(",")],
    queryFn: async () => {
      const others = recent.filter((h) => h !== handle).slice(0, 4);
      return Promise.all(others.map((h) => getProduct(h).catch(() => null)));
    },
    enabled: recent.length > 1,
  });

  if (isLoading || !product) {
    return <div className="px-5 sm:px-8 lg:px-12 py-20 grid lg:grid-cols-2 gap-12">
      <div className="aspect-[3/4] bg-[#EAE8E2] animate-pulse" />
      <div className="space-y-4"><div className="h-10 bg-[#EAE8E2] animate-pulse w-2/3" /><div className="h-6 bg-[#EAE8E2] animate-pulse w-1/3" /></div>
    </div>;
  }

  const variantForSize = (s) => product.variants.find((v) => v.size === s) || product.variants[0];
  const selectedVariant = size ? variantForSize(size) : null;
  const wished = isWished(product.handle);
  const hasSizes = product.sizes && product.sizes.length > 0;

  const add = () => {
    if (hasSizes && !size) { toast.error("Select a size first"); return; }
    const v = selectedVariant || product.variants[0];
    if (!v?.available) { toast.error("That size is sold out"); return; }
    addToCart({
      handle: product.handle, title: product.title, price: v.price, image: product.images?.[0],
      variantId: v.id, size: v.size || "One Size", color, quantity: 1,
    });
    toast.success("Added to your bag");
  };

  const addBundle = () => {
    if (!recs.length) return;
    [product, ...recs].slice(0, 4).forEach((p) => {
      const v = p.variants.find((x) => x.available) || p.variants[0];
      if (!v) return;
      addToCart({ handle: p.handle, title: p.title, price: v.price, image: p.images?.[0], variantId: v.id, size: v.size || "One Size", color: p.colors?.[0], quantity: 1 });
    });
    toast.success("The full fit is in your bag");
  };

  const cheapestVariant = (p) => p.variants.find((x) => x.available) || p.variants[0];
  const addQuick = (p) => {
    const v = cheapestVariant(p);
    if (!v) return;
    addToCart({ handle: p.handle, title: p.title, price: v.price, image: p.images?.[0], variantId: v.id, size: v.size || "One Size", color: p.colors?.[0], quantity: 1 });
    toast.success(`${p.title} added`);
  };
  const addPairSet = () => {
    if (hasSizes && !size) { toast.error("Pick your size for this piece first"); return; }
    add();
    pairs.forEach(addQuick);
    toast.success("The set is in your bag");
  };
  const pairTotal = [product, ...pairs].reduce((s, p) => s + (p?.price || 0), 0);

  return (
    <div>
      <div className="px-5 sm:px-8 lg:px-12 py-5 flex items-center gap-2 overline text-black/40">
        <Link to="/" className="hover:text-black">Home</Link><ChevronRight size={12} />
        <Link to={`/collections/${product.collections?.[0] || "new"}`} className="hover:text-black">{product.category}</Link><ChevronRight size={12} />
        <span className="text-black">{product.title}</span>
      </div>

      <section className="px-5 sm:px-8 lg:px-12 pb-16 grid lg:grid-cols-2 gap-8 lg:gap-14">
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          {product.images.length > 1 && (
            <div className="flex sm:flex-col gap-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                        className={`w-16 h-20 overflow-hidden bg-[#EAE8E2] border ${activeImg === i ? "border-black" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <motion.div key={activeImg} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                      className="flex-1 aspect-[3/4] overflow-hidden bg-[#EAE8E2]">
            <img src={product.images[activeImg]} alt={product.title} className="w-full h-full object-cover" data-testid="product-main-image" />
          </motion.div>
        </div>

        <div className="lg:py-4">
          {product.badge && <span className="overline bg-[#0A0A0A] text-[#F4F2ED] px-3 py-1.5">{product.badge}</span>}
          <h1 className="font-hero text-4xl sm:text-5xl mt-4 leading-tight" data-testid="product-title">{product.title}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="font-mono text-xl">{money(product.price)}</span>
            {product.compareAtPrice && <span className="font-mono text-sm text-black/40 line-through">{money(product.compareAtPrice)}</span>}
          </div>

          {product.available && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 overline text-black/50" data-testid="product-urgency">
              {product.preorder ? (
                <>
                  <span className="flex items-center gap-1.5"><Eye size={13} strokeWidth={1.5} /> {viewers} watching this drop</span>
                  <span className="flex items-center gap-1.5 text-[#0A0A0A]"><Flame size={13} strokeWidth={1.5} /> Preorder open · limited allocation</span>
                </>
              ) : (
                <>
                  <span className="flex items-center gap-1.5"><Eye size={13} strokeWidth={1.5} /> {viewers} viewing now</span>
                  <span className="flex items-center gap-1.5 text-[#0A0A0A]"><Flame size={13} strokeWidth={1.5} /> Only {stockLeft} left — selling fast</span>
                </>
              )}
            </div>
          )}

          {product.colors?.length > 0 && (
            <div className="mt-7">
              <span className="overline text-black/50">Color — {color}</span>
              <div className="flex gap-2 mt-2">
                {product.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`}
                          className={`px-4 py-2 overline border transition-colors ${color === c ? "bg-[#0A0A0A] text-[#F4F2ED] border-black" : "border-black/20 hover:border-black"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          {hasSizes && (
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <span className="overline text-black/50">Size</span>
                <button onClick={() => setSizeFinderOpen(true)} data-testid="open-size-finder" className="overline flex items-center gap-1 link-underline"><Ruler size={13} /> Size Finder</button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {product.sizes.map((s) => {
                  const v = variantForSize(s);
                  const disabled = v && !v.available;
                  return (
                    <button key={s} disabled={disabled} onClick={() => setSize(s)} data-testid={`size-${s}`}
                            className={`min-w-[52px] px-3 py-3 overline border transition-colors ${size === s ? "bg-[#0A0A0A] text-[#F4F2ED] border-black" : "border-black/20 hover:border-black"} ${disabled ? "opacity-30 line-through cursor-not-allowed" : ""}`}>{s}</button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            <button onClick={add} ref={addBtnRef} data-testid="add-to-cart-button" className="btn-fill flex-1">
              {product.preorder ? "Preorder Now" : product.available ? "Add to Bag" : "Sold Out"}
            </button>
            <button onClick={() => { toggleWishlist(product.handle); toast(wished ? "Removed from wishlist" : "Saved to wishlist"); }}
                    data-testid="product-wishlist-btn" className="btn-outline px-5" aria-label="Wishlist">
              <Heart size={18} className={wished ? "fill-[#0A0A0A]" : ""} />
            </button>
          </div>
          {product.preorder && (
            <p className="font-body text-sm text-black/60 mt-3" data-testid="preorder-note">
              This piece isn't in yet — secure your size now and it ships the moment the drop lands.
            </p>
          )}

          <div className="grid grid-cols-3 gap-3 mt-6 text-center">
            {[["Free ship $100+", Truck], ["Easy returns", RotateCcw], ["Secure checkout", ShieldCheck]].map(([t, Icon]) => (
              <div key={t} className="border border-black/10 py-4 flex flex-col items-center gap-1.5">
                <Icon size={17} strokeWidth={1.25} /><span className="font-mono text-[10px] text-black/50">{t}</span>
              </div>
            ))}
          </div>

          <Accordion type="single" collapsible className="mt-8" defaultValue="desc">
            <AccordionItem value="desc" className="border-black/10">
              <AccordionTrigger className="overline hover:no-underline">Details</AccordionTrigger>
              <AccordionContent className="font-body text-black/60 leading-relaxed">{product.description || "Premium construction, hand-finished detailing. A LEVINCHI world essential."}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="ship" className="border-black/10">
              <AccordionTrigger className="overline hover:no-underline">Shipping & Returns</AccordionTrigger>
              <AccordionContent className="font-body text-black/60 leading-relaxed">Free shipping on orders over $100. 14-day returns on unworn items. Checkout is secured by Shopify.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {pairs.length > 0 && (
        <section className="relative bg-[#0A0A0A] text-[#F4F2ED]" data-testid="pair-the-fit">
          <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
            <p className="overline text-white/50 flex items-center gap-2"><Layers size={14} /> Complete the look</p>
            <h2 className="font-hero text-4xl sm:text-6xl mt-2 mb-8">PAIR THE FIT</h2>

            <div className="flex flex-wrap items-stretch gap-4">
              {/* current item */}
              <PairTile p={product} highlight />
              {pairs.map((p) => (
                <React.Fragment key={p.handle}>
                  <div className="hidden sm:flex items-center font-hero text-4xl text-white/40">+</div>
                  <PairTile p={p} onAdd={() => addQuick(p)} />
                </React.Fragment>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 border-t border-white/10 pt-6">
              <div className="flex-1">
                <p className="overline text-white/40">Buy the set together</p>
                <p className="font-mono text-2xl mt-1">{money(pairTotal)}</p>
              </div>
              <button onClick={addPairSet} data-testid="add-set-btn" className="btn-fill bg-[#F4F2ED] text-[#0A0A0A] hover:bg-white">Add the Set to Bag</button>
              <button onClick={() => add()} data-testid="buy-separately-btn" className="btn-ghost-light">Add This Piece Only</button>
            </div>
          </div>
        </section>
      )}

      {recs.length > 0 && (
        <section className="px-5 sm:px-8 lg:px-12 py-16 border-t border-black/10">
          <h2 className="font-hero text-4xl sm:text-5xl mb-8">YOU MAY ALSO LIKE</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {recs.map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
          </div>
        </section>
      )}

      {recentProducts.filter(Boolean).length > 0 && (
        <section className="px-5 sm:px-8 lg:px-12 py-16 border-t border-black/10">
          <h2 className="font-hero text-4xl sm:text-5xl mb-8">RECENTLY VIEWED</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {recentProducts.filter(Boolean).map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
          </div>
        </section>
      )}
      {showSticky && (
        <motion.div initial={{ y: 80 }} animate={{ y: 0 }} data-testid="sticky-buy-bar"
                    className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10 text-[#F4F2ED]">
          <div className="px-4 sm:px-8 lg:px-12 lg:pr-52 py-3 flex items-center gap-4">
            <img src={product.images?.[0]} alt="" className="w-11 h-14 object-cover bg-[#141414] hidden sm:block" />
            <div className="flex-1 min-w-0">
              <p className="font-display text-xs uppercase tracking-tight truncate">{product.title}</p>
              <p className="font-mono text-sm text-[#FFFFFF]">{money(product.price)}{size ? ` · ${size}` : ""}</p>
            </div>
            <button onClick={add} data-testid="sticky-add-btn" className="btn-fill bg-[#FFFFFF] text-[#0A0A0A] hover:bg-[#FFFFFF] px-6 sm:px-10 py-3">
              {product.preorder ? "Preorder Now" : product.available ? "Add to Bag" : "Sold Out"}
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
